import {
  parseEvent,
  COMPANION_ARTIFACTS_MAX,
  COMPANION_ARTIFACT_NAME_MAX_LENGTH,
  COMPANION_DECISION_CALL_ID_MAX_LENGTH,
  COMPANION_DECISION_DETAIL_MAX_CODE_POINTS,
  COMPANION_DECISION_LABEL_MAX_LENGTH,
  COMPANION_DECISION_TITLE_MAX_LENGTH,
  COMPANION_REPLY_DELTA_MAX_CODE_POINTS,
  COMPANION_TURN_TEXT_MAX_CODE_POINTS,
  type CompanionArtifact,
  type CompanionBridgeEvent,
  type CompanionDecision,
  type CompanionMate,
  type CompanionTurn,
  type PersonaKey,
} from "@m8t-stack/companion-bridge-contract";
import {
  DECISION_REFUSAL_REASONS,
  isDecisionRefusalReason,
  parseUiDirectiveFrame,
  UI_DIRECTIVE_PART_TYPE,
  UI_DIRECTIVE_RESULT_PART_TYPE,
  type DecisionResultMessageV1,
  type PresentDecisionDirectiveV1,
} from "@m8t-stack/api-contract";
import { parseArtifacts } from "@m8t-stack/foundry-invoke";
import { stat } from "node:fs/promises";
import { getFoundryToken } from "./auth.js";
import { getConfigPath } from "./config.js";
import {
  resolveGatewayContext,
  type GatewayContext,
} from "./gateway-context.js";

const RESPONSE_MAX_BYTES = 1_000_000;
const REQUEST_DEADLINE_MS = 120_000;
const OPAQUE_ID = /^[A-Za-z0-9_-]{1,256}$/u;
const AGENT_NAME_MAX_LENGTH = 256;

export interface CompanionChatDeps {
  readonly fetch?: typeof fetch;
  readonly resolveGatewayContext?: typeof resolveGatewayContext;
  readonly getFoundryToken?: typeof getFoundryToken;
  readonly now?: () => number;
  readonly deadlineMs?: number;
  readonly installEpoch?: () => Promise<number | null>;
  /**
   * The agent roster and conversation binding are re-resolved on every
   * request by default. A serving bridge session injects caching wrappers
   * here so a warm request skips those round trips.
   */
  readonly fetchAgents?: typeof fetchAgents;
  readonly resolveConversationId?: typeof resolveConversationId;
}

export interface CompanionSend {
  readonly requestId: string;
  readonly personaKey: PersonaKey;
  readonly draftRevision: number;
  readonly text: string;
}

export interface CompanionHistoryRequest {
  readonly requestId: string;
  readonly personaKey: PersonaKey;
  readonly limit: number;
}

export interface CompanionDecide {
  readonly requestId: string;
  readonly personaKey: PersonaKey;
  readonly callId: string;
  readonly optionIndex: number;
}

type EventSink = (event: CompanionBridgeEvent) => void;
type FailureReason = Extract<
  CompanionBridgeEvent,
  { type: "failed" }
>["reason"];

class PredispatchFailure extends Error {
  constructor(readonly reason: FailureReason) {
    super("companion bridge pre-dispatch failure");
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function isAgentName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= AGENT_NAME_MAX_LENGTH &&
    !hasControlCharacter(value)
  );
}

function authenticatedHeaders(token: string): Record<string, string> {
  return {
    accept: "application/json",
    authorization: `Bearer ${token}`,
  };
}

function gatewayOrigin(gateway: GatewayContext): string {
  let url: URL;
  try {
    url = new URL(gateway.gatewayUrl);
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    throw new PredispatchFailure("not-connected");
  }
  return url.origin;
}

async function readBoundedText(
  response: Response,
  maxBytes = RESPONSE_MAX_BYTES,
): Promise<string> {
  const declared = response.headers.get("content-length");
  if (declared !== null && Number(declared) > maxBytes) {
    throw new Error("bounded response rejected");
  }
  if (!response.body) throw new Error("response body unavailable");

  const reader = (
    response.body as ReadableStream<Uint8Array>
  ).getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let total = 0;
  let text = "";
  for (;;) {
    const part = await reader.read();
    if (part.done) break;
    total += part.value.byteLength;
    if (total > maxBytes) {
      await reader.cancel().catch(() => undefined);
      throw new Error("bounded response rejected");
    }
    text += decoder.decode(part.value, { stream: true });
  }
  text += decoder.decode();
  return text;
}

async function readJsonEnvelope(response: Response): Promise<unknown> {
  if (!response.ok) {
    const reason =
      response.status === 401 || response.status === 403
        ? "not-authorized"
        : response.status === 409
          ? "busy"
          : "not-connected";
    throw new PredispatchFailure(reason);
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new PredispatchFailure("not-connected");
  }

  let envelope: unknown;
  try {
    envelope = JSON.parse(await readBoundedText(response));
  } catch (error) {
    if (error instanceof PredispatchFailure) throw error;
    throw new PredispatchFailure("not-connected");
  }
  if (!isRecord(envelope) || envelope.ok !== true || !("data" in envelope)) {
    throw new PredispatchFailure("not-connected");
  }
  return envelope.data;
}

function decodeAgents(data: unknown): readonly Record<string, unknown>[] {
  if (!isRecord(data) || !Array.isArray(data.agents)) {
    throw new PredispatchFailure("mate-unavailable");
  }
  return data.agents.filter(isRecord);
}

/**
 * The deployed agent for a persona: its name, or null when nothing serves it.
 *
 * Absence and ambiguity are different answers and must stay different. Nothing
 * deployed for a persona is an ABSENCE — an Ezra-only install simply has no
 * Stacey — and the roster reports the mates that exist. TWO agents claiming one
 * persona is a FAULT for THAT persona: there is no answer to which one a message
 * would reach, so this refuses rather than picking one.
 *
 * Collapsing the two is what made an Ezra-only install report the entire
 * companion as "not connected", every head greyed, on a healthy platform.
 *
 * The persona filter is on `personaKey` ALONE. It used to also require a
 * well-formed name, which quietly turned a third state into the first: an agent
 * deployed under an unusable name (control characters, absurd length) matched
 * nothing and read as "no mate deployed", so the desktop reported an absence
 * about an agent that is right there. It is not absent and it is not ambiguous —
 * it is deployed and unreachable, which is `mate-unavailable`, and it is now
 * classified after the filter where it can be seen.
 */
function resolveAgentName(
  agents: readonly Record<string, unknown>[],
  personaKey: PersonaKey,
): string | null {
  const matches = agents.filter((agent) => agent.personaKey === personaKey);
  if (matches.length === 0) return null;
  if (matches.length > 1) {
    throw new PredispatchFailure("mate-ambiguous");
  }
  const match = matches[0];
  if (!isAgentName(match.name)) {
    throw new PredispatchFailure("mate-unavailable");
  }
  return match.name;
}

/** `resolveAgentName`, for the paths that cannot proceed without one. */
function requireAgentName(
  agents: readonly Record<string, unknown>[],
  personaKey: PersonaKey,
): string {
  const name = resolveAgentName(agents, personaKey);
  if (name === null) throw new PredispatchFailure("mate-unavailable");
  return name;
}

function decodeConversationId(data: unknown): string {
  if (
    !isRecord(data) ||
    typeof data.id !== "string" ||
    !OPAQUE_ID.test(data.id)
  ) {
    throw new PredispatchFailure("not-connected");
  }
  return data.id;
}

function decodeLastMessageId(data: unknown): string | undefined {
  if (!isRecord(data) || !Array.isArray(data.messages)) {
    throw new PredispatchFailure("not-connected");
  }
  const messages = data.messages as readonly unknown[];
  const last: unknown = messages.at(-1);
  if (
    !isRecord(last) ||
    typeof last.id !== "string" ||
    !OPAQUE_ID.test(last.id)
  ) {
    return undefined;
  }
  return last.id;
}

export async function resolveConversationId(
  fetchImpl: typeof fetch,
  origin: string,
  headers: Record<string, string>,
  agentName: string,
  signal: AbortSignal,
): Promise<string> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/api/conversations`, {
      method: "POST",
      headers: { ...headers, "content-type": "application/json" },
      body: JSON.stringify({ agentName }),
      signal,
    });
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return decodeConversationId(await readJsonEnvelope(response));
}

async function fetchConversationMessages(
  fetchImpl: typeof fetch,
  origin: string,
  headers: Record<string, string>,
  conversationId: string,
  signal: AbortSignal,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetchImpl(
      `${origin}/api/conversations/${encodeURIComponent(conversationId)}/messages`,
      { headers, signal },
    );
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return readJsonEnvelope(response);
}

/** Timestamps sane enough to format: positive epoch seconds this side of 5138. */
const EPOCH_SECONDS_LIMIT = 100_000_000_000;

/** Line breaks and tabs stay; every other control character is dropped. */
function sanitizeMessageText(value: string): string {
  let out = "";
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    const forbidden =
      code === 0x7f ||
      (code <= 0x1f && code !== 0x09 && code !== 0x0a && code !== 0x0d);
    if (!forbidden) out += character;
  }
  return out;
}

function boundCodePoints(value: string, maxCodePoints: number): string {
  let count = 0;
  let out = "";
  for (const character of value) {
    if (count >= maxCodePoints) return out;
    out += character;
    count += 1;
  }
  return value;
}

/**
 * One line the bridge contract will carry: every control character becomes a
 * space, then the length bound applies. Empty means unusable, never repaired.
 */
function boundPlainLine(value: string, maxLength: number): string {
  return value
    // eslint-disable-next-line no-control-regex -- replacing the C0 controls is exactly this sanitizer's job
    .replace(/[\u0000-\u001f\u007f]/gu, " ")
    .trim()
    .slice(0, maxLength);
}

/** The wire `type` of the stream part that carries a hosted reply's files. */
const ARTIFACTS_PART_TYPE = "data-artifacts";

/**
 * The gateway's directive as the decision the bridge carries, or null when it
 * is not one a card can be drawn from. Only the three states the gateway
 * durably streams exist on the bridge; `submitting` and `error` are a
 * client's own transients and never arrive here.
 */
function toCompanionDecision(
  directive: PresentDecisionDirectiveV1,
): CompanionDecision | null {
  const state = directive.state;
  if (
    state.status !== "pending" &&
    state.status !== "selected" &&
    state.status !== "dismissed"
  ) {
    return null;
  }
  const callId = boundPlainLine(
    directive.callId,
    COMPANION_DECISION_CALL_ID_MAX_LENGTH,
  );
  const title = boundPlainLine(
    directive.args.title,
    COMPANION_DECISION_TITLE_MAX_LENGTH,
  );
  if (callId.length === 0 || title.length === 0) return null;
  const options = [];
  for (const option of directive.args.options) {
    const label = boundPlainLine(option.label, COMPANION_DECISION_LABEL_MAX_LENGTH);
    const detail = boundCodePoints(
      sanitizeMessageText(option.detail).trim(),
      COMPANION_DECISION_DETAIL_MAX_CODE_POINTS,
    );
    // An option that lost its words is not choosable, and a card missing one
    // of its options is not the decision that was asked.
    if (label.length === 0 || detail.length === 0) return null;
    options.push({ label, detail });
  }
  const base = { callId, title, options };
  return state.status === "selected"
    ? { ...base, status: "selected", optionIndex: state.optionIndex }
    : { ...base, status: state.status };
}

/**
 * The chip-sized artifact list from a `data-artifacts` part. An entry that
 * cannot name itself is dropped; sizes travel only when sane.
 */
function toCompanionArtifacts(data: readonly unknown[]): CompanionArtifact[] {
  const artifacts: CompanionArtifact[] = [];
  for (const entry of data) {
    if (artifacts.length >= COMPANION_ARTIFACTS_MAX) break;
    if (!isRecord(entry) || typeof entry.name !== "string") continue;
    const name = boundPlainLine(entry.name, COMPANION_ARTIFACT_NAME_MAX_LENGTH);
    if (name.length === 0) continue;
    const size = entry.size_bytes;
    artifacts.push(
      typeof size === "number" && Number.isSafeInteger(size) && size >= 0
        ? { name, sizeBytes: size }
        : { name },
    );
  }
  return artifacts;
}

function decodeMessageInstant(value: unknown): string | null {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    value > 0 &&
    value < EPOCH_SECONDS_LIMIT
    ? new Date(Math.round(value) * 1000).toISOString()
    : null;
}

/**
 * The turns the panel shows, decoded defensively: only user and mate text
 * messages, cleaned to what the bridge contract will carry, capped to the
 * newest `limit`. An entry that cannot be rendered is skipped, never fatal.
 */
function decodeTurns(data: unknown, limit: number): CompanionTurn[] {
  if (!isRecord(data) || !Array.isArray(data.messages)) {
    throw new PredispatchFailure("not-connected");
  }
  const turns: CompanionTurn[] = [];
  for (const entry of data.messages as readonly unknown[]) {
    if (!isRecord(entry)) continue;
    if (entry.role !== "user" && entry.role !== "assistant") continue;
    if (typeof entry.id !== "string" || !OPAQUE_ID.test(entry.id)) continue;
    if (typeof entry.content !== "string") continue;
    const text = boundCodePoints(
      sanitizeMessageText(entry.content),
      COMPANION_TURN_TEXT_MAX_CODE_POINTS,
    );
    if (text.length === 0) continue;
    turns.push({
      id: entry.id,
      role: entry.role === "user" ? "user" : "mate",
      text,
      at: decodeMessageInstant(entry.createdAt),
    });
  }
  return turns.slice(-limit);
}

/**
 * A decision rebuilt from the durable transcript. The gateway folds every
 * decision's history into one snapshot per call and carries it on an
 * otherwise-empty message; an unreadable snapshot yields null and the entry
 * renders as nothing rather than as a broken card.
 */
function decodeDurableDecision(
  entry: Record<string, unknown>,
): CompanionDecision | null {
  const frame = parseUiDirectiveFrame({
    directive: entry.uiDirective,
    source: entry.uiDirectiveSource === "voice" ? "voice" : "text",
  });
  if (!frame) return null;
  return toCompanionDecision(frame.directive);
}

/**
 * `decodeTurns`, with the durable decisions and files kept: a message
 * carrying a decision snapshot becomes a decision turn in its transcript
 * position, and a persisted reply that ends in an artifact manifest becomes
 * its clean text plus chips — never the raw manifest as prose.
 */
function decodeRichTurns(data: unknown, limit: number): CompanionTurn[] {
  if (!isRecord(data) || !Array.isArray(data.messages)) {
    throw new PredispatchFailure("not-connected");
  }
  const turns: CompanionTurn[] = [];
  for (const entry of data.messages as readonly unknown[]) {
    if (!isRecord(entry)) continue;
    if (entry.role !== "user" && entry.role !== "assistant") continue;
    if (typeof entry.id !== "string" || !OPAQUE_ID.test(entry.id)) continue;
    if (typeof entry.content !== "string") continue;
    // The manifest is read before any bounding: a cap that cut the fence in
    // half would leave broken JSON rendering as the message.
    const parsed = parseArtifacts(entry.content);
    const artifacts = toCompanionArtifacts(parsed.artifacts);
    const text = boundCodePoints(
      sanitizeMessageText(parsed.text),
      COMPANION_TURN_TEXT_MAX_CODE_POINTS,
    );
    const at = decodeMessageInstant(entry.createdAt);
    const decision =
      entry.uiDirective === undefined ? null : decodeDurableDecision(entry);
    if (decision !== null) {
      // A decision is the mate's to raise, whatever row carried it.
      turns.push({ id: entry.id, role: "mate", text, at, decision });
      continue;
    }
    if (text.length === 0 && artifacts.length === 0) continue;
    turns.push({
      id: entry.id,
      role: entry.role === "user" ? "user" : "mate",
      text,
      at,
      ...(artifacts.length > 0 ? { artifacts } : {}),
    });
  }
  return turns.slice(-limit);
}

function mateRoute(
  personaKey: PersonaKey,
  desktopAfter?: string,
): string {
  const marker =
    desktopAfter === undefined ? "" : `&desktopAfter=${desktopAfter}`;
  return `/mates/${personaKey}?desktopPending=1${marker}`;
}

function emitFailure(
  sink: EventSink,
  reason: FailureReason,
  requestId?: string,
): CompanionBridgeEvent {
  const event = parseEvent(
    requestId === undefined
      ? { type: "failed", reason }
      : { type: "failed", requestId, reason },
  );
  sink(event);
  return event;
}

function classifyUnknownPredispatch(error: unknown): FailureReason {
  return error instanceof PredispatchFailure
    ? error.reason
    : "not-connected";
}

/**
 * The gateway accepts both its own API audience and the Foundry audience, but
 * an interactive caller's bearer is forwarded to Foundry unchanged. A token
 * minted for `api://<gatewayClientId>` therefore reaches Foundry with the wrong
 * audience and is rejected, so every companion request carries the Foundry
 * audience — the same one the web product's sign-in asks for.
 */
async function acquireContext(
  deps: CompanionChatDeps,
): Promise<{ token: string; origin: string }> {
  let gateway: GatewayContext;
  let token: string;
  try {
    gateway = await (deps.resolveGatewayContext ?? resolveGatewayContext)({
      interactive: false,
    });
    token = await (deps.getFoundryToken ?? getFoundryToken)();
  } catch {
    throw new PredispatchFailure("not-authorized");
  }
  return { token, origin: gatewayOrigin(gateway) };
}

export async function fetchAgents(
  fetchImpl: typeof fetch,
  origin: string,
  token: string,
  signal: AbortSignal,
): Promise<readonly Record<string, unknown>[]> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/api/agents`, {
      headers: authenticatedHeaders(token),
      signal,
    });
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return decodeAgents(await readJsonEnvelope(response));
}

function createDeadline(
  deps: CompanionChatDeps,
): { controller: AbortController; timer: ReturnType<typeof setTimeout> } {
  const controller = new AbortController();
  const timer = setTimeout(
    () => {
      controller.abort();
    },
    deps.deadlineMs ?? REQUEST_DEADLINE_MS,
  );
  return { controller, timer };
}

/**
 * When m8t was installed on this workstation, taken from the earliest
 * timestamp on the CLI configuration file. The mates have been reachable since
 * the platform installation that created it, so this is the honest lower bound
 * for "Active for …". Returns null when the file cannot be read.
 */
async function readInstallEpoch(): Promise<number | null> {
  try {
    const stats = await stat(getConfigPath());
    const candidates = [stats.birthtimeMs, stats.mtimeMs].filter(
      (value) => Number.isFinite(value) && value > 0,
    );
    if (candidates.length === 0) return null;
    return Math.min(...candidates);
  } catch {
    return null;
  }
}

export async function rosterCompanions(
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  try {
    const { token, origin } = await acquireContext(deps);
    const agents = await (deps.fetchAgents ?? fetchAgents)(
      deps.fetch ?? fetch,
      origin,
      token,
      controller.signal,
    );
    const now = (deps.now ?? Date.now)();
    const installed = await (deps.installEpoch ?? readInstallEpoch)();
    // Never claim an age we cannot stand behind: an unreadable or
    // future-dated install stamp falls back to this moment.
    const activeSince = new Date(
      installed === null || installed > now ? now : installed,
    ).toISOString();
    // Every mate the desktop can present, in display order, kept only where the
    // deployment actually has one. The roster is a report of what is there.
    const PRESENTABLE = [
      { personaKey: "startup-advisor", displayName: "Stacey", portraitKey: "stacey" },
      { personaKey: "ezra", displayName: "Ezra", portraitKey: "ezra" },
    ] as const;
    //
    // A mate that cannot be resolved takes ITSELF out of the roster and nothing
    // else. It used to take the whole read down: `resolveAgentName` threw, the
    // throw left this function, the desktop saw no roster event and greyed every
    // head — so two agents claiming Stacey cost the founder a working Ezra, on a
    // gateway answering perfectly. The reason is remembered rather than thrown,
    // because it is the only thing that can explain an empty roster further down.
    const faults: FailureReason[] = [];
    const mates: readonly CompanionMate[] = PRESENTABLE.flatMap((mate) => {
      let agentName: string | null;
      try {
        agentName = resolveAgentName(agents, mate.personaKey);
      } catch (error) {
        if (!(error instanceof PredispatchFailure)) throw error;
        faults.push(error.reason);
        return [];
      }
      return agentName === null ? [] : [{ ...mate, agentName, activeSince }];
    });
    // No known mate deployed is not an empty desktop, it is no desktop: there is
    // nothing to show and nothing to send to. The contract refuses an empty roster,
    // so say why here rather than letting it surface as a parse error — and say
    // WHICH why: an empty roster caused by a collision is a different problem, with
    // a different remedy, from one caused by nothing being deployed.
    if (mates.length === 0) {
      throw new PredispatchFailure(
        faults.includes("mate-ambiguous") ? "mate-ambiguous" : "mate-unavailable",
      );
    }
    const event = parseEvent({ type: "roster", origin, mates });
    sink(event);
    return event;
  } catch (error) {
    return emitFailure(sink, classifyUnknownPredispatch(error));
  } finally {
    clearTimeout(timer);
  }
}

async function drainAcceptedSse(response: Response): Promise<void> {
  const text = await readBoundedText(response);
  const frames = text.split(/\r?\n\r?\n/u);
  let sawDone = false;

  for (const frame of frames) {
    if (frame.trim().length === 0) continue;
    const dataLines = frame
      .split(/\r?\n/u)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart());
    if (dataLines.length === 0) continue;
    const data = dataLines.join("\n");
    if (sawDone) throw new Error("data after terminal stream marker");
    if (data === "[DONE]") {
      sawDone = true;
      continue;
    }
    const parsed = JSON.parse(data) as unknown;
    if (
      !isRecord(parsed) ||
      typeof parsed.type !== "string" ||
      parsed.type === "error"
    ) {
      throw new Error("invalid stream event");
    }
  }

  if (!sawDone) throw new Error("stream ended without terminal marker");
}

/** Coalesce deltas so event volume stays sane without the stream feeling dead. */
const REPLY_FLUSH_MIN_LENGTH = 64;
const REPLY_FLUSH_MS = 150;

function emitBoundedText(text: string, emit: (slice: string) => void): void {
  let slice = "";
  let count = 0;
  for (const character of text) {
    slice += character;
    count += 1;
    if (count === COMPANION_REPLY_DELTA_MAX_CODE_POINTS) {
      emit(slice);
      slice = "";
      count = 0;
    }
  }
  if (slice.length > 0) emit(slice);
}

/**
 * The same accepted-stream discipline as the draining reader, applied
 * incrementally: frames are parsed as they arrive so the text deltas can be
 * forwarded live rather than after the whole reply has landed. `onData`
 * receives every `data-*` part; pending text is flushed first so what was
 * said before a card or a file list stays before it.
 */
async function streamAcceptedSse(
  response: Response,
  now: () => number,
  onText: (text: string) => void,
  onData?: (part: Record<string, unknown>) => void,
): Promise<void> {
  if (!response.body) throw new Error("response body unavailable");
  const reader = (
    response.body as ReadableStream<Uint8Array>
  ).getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let buffered = "";
  let total = 0;
  const terminal = { sawDone: false };
  let pending = "";
  let lastFlush = now();

  const flush = (): void => {
    const text = sanitizeMessageText(pending);
    pending = "";
    lastFlush = now();
    if (text.length > 0) emitBoundedText(text, onText);
  };

  const handleFrame = (frame: string): void => {
    if (frame.trim().length === 0) return;
    const dataLines = frame
      .split(/\r?\n/u)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart());
    if (dataLines.length === 0) return;
    const data = dataLines.join("\n");
    if (terminal.sawDone) throw new Error("data after terminal stream marker");
    if (data === "[DONE]") {
      terminal.sawDone = true;
      return;
    }
    const parsed = JSON.parse(data) as unknown;
    if (
      !isRecord(parsed) ||
      typeof parsed.type !== "string" ||
      parsed.type === "error"
    ) {
      throw new Error("invalid stream event");
    }
    if (
      parsed.type === "text-delta" &&
      typeof parsed.delta === "string" &&
      parsed.delta.length > 0
    ) {
      pending += parsed.delta;
      if (
        pending.length >= REPLY_FLUSH_MIN_LENGTH ||
        now() - lastFlush >= REPLY_FLUSH_MS
      ) {
        flush();
      }
    }
    if (onData && parsed.type.startsWith("data-")) {
      flush();
      onData(parsed);
    }
  };

  const drainFrames = (): void => {
    for (;;) {
      const boundary = /\r?\n\r?\n/u.exec(buffered);
      if (!boundary) return;
      const frame = buffered.slice(0, boundary.index);
      buffered = buffered.slice(boundary.index + boundary[0].length);
      handleFrame(frame);
    }
  };

  for (;;) {
    const part = await reader.read();
    if (part.done) break;
    total += part.value.byteLength;
    if (total > RESPONSE_MAX_BYTES) {
      await reader.cancel().catch(() => undefined);
      throw new Error("bounded response rejected");
    }
    buffered += decoder.decode(part.value, { stream: true });
    drainFrames();
    if (terminal.sawDone) {
      // The stream has said everything it will say; a connection a proxy
      // holds open past this point must not stall a finished reply.
      await reader.cancel().catch(() => undefined);
      break;
    }
  }
  if (!terminal.sawDone) {
    buffered += decoder.decode();
    drainFrames();
    if (buffered.trim().length > 0) handleFrame(buffered);
  }
  flush();
  if (!terminal.sawDone) {
    throw new Error("stream ended without terminal marker");
  }
}

async function dispatchConversation(
  request: { readonly requestId: string; readonly personaKey: PersonaKey },
  sink: EventSink,
  deps: CompanionChatDeps,
  consume: (response: Response) => Promise<void>,
  readDesktopMarker: boolean,
  chatBody: (agentName: string, conversationId: string) => unknown,
  /**
   * Reads a definitive refusal off a rejected chat response. Returning a
   * reason closes the exchange as that failure — the gateway answered, and
   * nothing reached the worker. Returning null keeps the honest default: a
   * rejection whose meaning is unknown stays indeterminate.
   */
  classifyRefusal?: (response: Response) => Promise<FailureReason | null>,
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  const fetchImpl = deps.fetch ?? fetch;
  let route: string | undefined;
  let dispatched = false;
  let accepted = false;

  try {
    const { token, origin } = await acquireContext(deps);
    const headers = authenticatedHeaders(token);
    const agents = await (deps.fetchAgents ?? fetchAgents)(
      fetchImpl,
      origin,
      token,
      controller.signal,
    );
    // Sending needs a mate to send TO. Absent is tolerable in the roster (it just
    // is not listed) but not here: there is no agent to address, which is exactly
    // what mate-unavailable means.
    const agentName = requireAgentName(agents, request.personaKey);
    const conversationId = await (deps.resolveConversationId ?? resolveConversationId)(
      fetchImpl,
      origin,
      headers,
      agentName,
      controller.signal,
    );
    // The marker anchors the browser handoff's pending-turn display. A
    // conversing panel never opens the browser, so it skips this read and a
    // whole authenticated round trip with it.
    const desktopAfter = readDesktopMarker
      ? decodeLastMessageId(
          await fetchConversationMessages(
            fetchImpl,
            origin,
            headers,
            conversationId,
            controller.signal,
          ),
        )
      : undefined;
    route = mateRoute(request.personaKey, desktopAfter);

    const dispatching = parseEvent({
      type: "dispatching",
      requestId: request.requestId,
      personaKey: request.personaKey,
      route,
    });
    sink(dispatching);
    dispatched = true;

    let chatResponse: Response;
    try {
      chatResponse = await fetchImpl(`${origin}/api/chat`, {
        method: "POST",
        headers: { ...headers, "content-type": "application/json" },
        body: JSON.stringify(chatBody(agentName, conversationId)),
        signal: controller.signal,
      });
    } catch {
      throw new Error("chat dispatch outcome unavailable");
    }

    if (
      !chatResponse.ok ||
      !chatResponse.body ||
      !(chatResponse.headers.get("content-type") ?? "")
        .toLowerCase()
        .includes("text/event-stream")
    ) {
      if (!chatResponse.ok && classifyRefusal) {
        const reason = await classifyRefusal(chatResponse);
        if (reason !== null) {
          return emitFailure(sink, reason, request.requestId);
        }
      }
      throw new Error("chat response not accepted");
    }

    const acceptedEvent = parseEvent({
      type: "accepted",
      requestId: request.requestId,
      personaKey: request.personaKey,
      route,
    });
    sink(acceptedEvent);
    accepted = true;

    await consume(chatResponse);
    const completed = parseEvent({
      type: "completed",
      requestId: request.requestId,
    });
    sink(completed);
    return completed;
  } catch (error) {
    if (!dispatched) {
      return emitFailure(
        sink,
        classifyUnknownPredispatch(error),
        request.requestId,
      );
    }
    const indeterminate = parseEvent({
      type: "indeterminate",
      requestId: request.requestId,
      stage: accepted ? "after-accept" : "dispatch",
      route: route ?? mateRoute(request.personaKey),
    });
    sink(indeterminate);
    return indeterminate;
  } finally {
    clearTimeout(timer);
  }
}

/** The ordinary chat body: one user turn addressed to the resolved mate. */
function textTurnBody(
  text: string,
): (agentName: string, conversationId: string) => unknown {
  return (agentName, conversationId) => ({
    messages: [{ role: "user", content: text }],
    agentName,
    conversationId,
  });
}

/**
 * Forwards the reply's `data-*` parts as bridge events. A frame this contract
 * version cannot read is dropped rather than surfaced half-typed — the
 * exchange still ends, just without the card or the chips.
 */
function replyDataForwarder(
  requestId: string,
  sink: EventSink,
): (part: Record<string, unknown>) => void {
  return (part) => {
    if (part.type === UI_DIRECTIVE_PART_TYPE) {
      const frame = parseUiDirectiveFrame(part.data);
      if (!frame) return;
      const decision = toCompanionDecision(frame.directive);
      if (decision === null) return;
      sink(parseEvent({ type: "reply-decision", requestId, decision }));
      return;
    }
    if (part.type === ARTIFACTS_PART_TYPE && Array.isArray(part.data)) {
      const artifacts = toCompanionArtifacts(part.data);
      if (artifacts.length > 0) {
        sink(parseEvent({ type: "reply-artifacts", requestId, artifacts }));
      }
    }
  };
}

export async function sendCompanionMessage(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return dispatchConversation(
    request,
    sink,
    deps,
    drainAcceptedSse,
    true,
    textTurnBody(request.text),
  );
}

/**
 * `send`, with the mate's answer carried back: the accepted stream's text
 * deltas are forwarded as reply-delta events instead of being discarded.
 */
export async function converseCompanionMessage(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return dispatchConversation(
    request,
    sink,
    deps,
    (response) =>
      streamAcceptedSse(response, deps.now ?? Date.now, (text) => {
        sink(
          parseEvent({
            type: "reply-delta",
            requestId: request.requestId,
            text,
          }),
        );
      }),
    false,
    textTurnBody(request.text),
  );
}

/**
 * `converse`, with the reply's decisions and artifacts forwarded instead of
 * dropped. A decision turn carries no text at all, so under `converse` it
 * reached the desktop as pure silence.
 */
export async function chatCompanionMessage(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return dispatchConversation(
    request,
    sink,
    deps,
    (response) =>
      streamAcceptedSse(
        response,
        deps.now ?? Date.now,
        (text) => {
          sink(
            parseEvent({
              type: "reply-delta",
              requestId: request.requestId,
              text,
            }),
          );
        },
        replyDataForwarder(request.requestId, sink),
      ),
    false,
    textTurnBody(request.text),
  );
}

/**
 * Reads a definitive decision refusal off a rejected answer dispatch. The
 * refusal reasons are the chat route's own constants; anything else falls
 * back to what the status alone can honestly say, and a rejection that says
 * nothing readable stays null so the caller keeps it indeterminate.
 */
async function classifyDecideRefusal(
  response: Response,
): Promise<FailureReason | null> {
  let reason: unknown;
  try {
    const envelope = JSON.parse(await readBoundedText(response)) as unknown;
    if (
      isRecord(envelope) &&
      isRecord(envelope.error) &&
      isRecord(envelope.error.details)
    ) {
      reason = envelope.error.details.reason;
    }
  } catch {
    /* an unreadable body leaves the status to speak for itself */
  }
  if (isDecisionRefusalReason(reason)) {
    return reason === DECISION_REFUSAL_REASONS.notPending
      ? "decision-not-pending"
      : "bad-request";
  }
  if (response.status === 401 || response.status === 403) {
    return "not-authorized";
  }
  if (response.status === 400) return "bad-request";
  if (response.status === 409) return "busy";
  return null;
}

/**
 * Answer a pending decision. The answer rides the same chat route that
 * delivered the card, as a result part with no text — a message with both
 * would read as an ordinary turn and dismiss the card instead of settling
 * it. The reply streams back like `chat`: the settled card first, then the
 * mate's follow-up.
 */
export async function decideCompanionMessage(
  request: CompanionDecide,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  const message: DecisionResultMessageV1 = {
    role: "user",
    parts: [
      {
        type: UI_DIRECTIVE_RESULT_PART_TYPE,
        data: {
          version: 1,
          name: "present_decision",
          callId: request.callId,
          result: { status: "selected", optionIndex: request.optionIndex },
        },
      },
    ],
  };
  return dispatchConversation(
    request,
    sink,
    deps,
    (response) =>
      streamAcceptedSse(
        response,
        deps.now ?? Date.now,
        (text) => {
          sink(
            parseEvent({
              type: "reply-delta",
              requestId: request.requestId,
              text,
            }),
          );
        },
        replyDataForwarder(request.requestId, sink),
      ),
    false,
    (agentName, conversationId) => ({
      messages: [message],
      agentName,
      conversationId,
    }),
    classifyDecideRefusal,
  );
}

/**
 * The newest turns of the mate's canonical conversation, one turn event per
 * message, oldest first, closed by history-end.
 */
export async function historyCompanionMessages(
  request: CompanionHistoryRequest,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return fetchConversationTurns(request, sink, deps, decodeTurns);
}

/**
 * `history`, with the conversation's decisions included as decision turns —
 * which is what lets a card survive a panel reopen, and what stops an
 * answered question being asked again.
 */
export async function recallCompanionMessages(
  request: CompanionHistoryRequest,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return fetchConversationTurns(request, sink, deps, decodeRichTurns);
}

async function fetchConversationTurns(
  request: CompanionHistoryRequest,
  sink: EventSink,
  deps: CompanionChatDeps,
  decode: (data: unknown, limit: number) => CompanionTurn[],
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  const fetchImpl = deps.fetch ?? fetch;
  try {
    const { token, origin } = await acquireContext(deps);
    const headers = authenticatedHeaders(token);
    const agents = await (deps.fetchAgents ?? fetchAgents)(
      fetchImpl,
      origin,
      token,
      controller.signal,
    );
    // Sending needs a mate to send TO. Absent is tolerable in the roster (it just
    // is not listed) but not here: there is no agent to address, which is exactly
    // what mate-unavailable means.
    const agentName = requireAgentName(agents, request.personaKey);
    const conversationId = await (deps.resolveConversationId ?? resolveConversationId)(
      fetchImpl,
      origin,
      headers,
      agentName,
      controller.signal,
    );
    const turns = decode(
      await fetchConversationMessages(
        fetchImpl,
        origin,
        headers,
        conversationId,
        controller.signal,
      ),
      request.limit,
    );
    for (const turn of turns) {
      sink(parseEvent({ type: "turn", requestId: request.requestId, turn }));
    }
    const end = parseEvent({
      type: "history-end",
      requestId: request.requestId,
      count: turns.length,
    });
    sink(end);
    return end;
  } catch (error) {
    return emitFailure(
      sink,
      classifyUnknownPredispatch(error),
      request.requestId,
    );
  } finally {
    clearTimeout(timer);
  }
}
